/**
 * Export/Import als reine Client-Funktion — kein Server nötig, funktioniert
 * offline. Der Anlass: alle Daten liegen nur in `localStorage` auf einem
 * Gerät (siehe speicher.ts). Ohne Sicherung heißt "Handy verloren oder
 * kaputt" gleich "alle Protokolle weg".
 */
import { BRAND } from './brand';
import { ladeAllesFuerExport, ladeObjekte, ladeProtokolle, speichereObjekte, speichereProtokolle } from './speicher';
import type { Objekt, Protokoll } from './domain/types';

interface Sicherung {
	version: 1;
	erzeugtAm: string;
	objekte: Objekt[];
	protokolle: Protokoll[];
}

export function exportiereAlles(): void {
	const { objekte, protokolle } = ladeAllesFuerExport();
	const sicherung: Sicherung = { version: 1, erzeugtAm: new Date().toISOString(), objekte, protokolle };
	const blob = new Blob([JSON.stringify(sicherung, null, 2)], { type: 'application/json' });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	const datum = new Date().toISOString().slice(0, 10);
	link.href = url;
	link.download = `${BRAND.shortName.toLowerCase()}-sicherung-${datum}.json`;
	link.click();
	URL.revokeObjectURL(url);
}

function istGueltigeSicherung(wert: unknown): wert is Sicherung {
	if (typeof wert !== 'object' || wert === null) return false;
	const kandidat = wert as Record<string, unknown>;
	return Array.isArray(kandidat.objekte) && Array.isArray(kandidat.protokolle);
}

export interface ImportErgebnis {
	erfolg: boolean;
	fehler?: string;
	neueObjekte?: number;
	neueProtokolle?: number;
}

/**
 * Fügt Objekte/Protokolle aus einer Sicherungsdatei hinzu, ohne
 * Bestehendes zu überschreiben. Duplikate (gleiche ID, z. B. bei
 * zweimaligem Import derselben Datei) werden übersprungen.
 */
export async function importiereDatei(datei: File): Promise<ImportErgebnis> {
	let inhalt: string;
	try {
		inhalt = await datei.text();
	} catch {
		return { erfolg: false, fehler: 'Datei konnte nicht gelesen werden.' };
	}

	let geparst: unknown;
	try {
		geparst = JSON.parse(inhalt);
	} catch {
		return { erfolg: false, fehler: 'Keine gültige Sicherungsdatei (kein gültiges JSON).' };
	}

	if (!istGueltigeSicherung(geparst)) {
		return { erfolg: false, fehler: 'Keine gültige Sicherungsdatei.' };
	}

	const bestehendeObjekte = ladeObjekte();
	const bestehendeObjektIds = new Set(bestehendeObjekte.map((o) => o.id));
	const hinzugefuegteObjekte = geparst.objekte.filter((o) => !bestehendeObjektIds.has(o.id));
	speichereObjekte([...bestehendeObjekte, ...hinzugefuegteObjekte]);

	const bestehendeProtokolle = ladeProtokolle();
	const bestehendeProtokollIds = new Set(bestehendeProtokolle.map((p) => p.id));
	const hinzugefuegteProtokolle = geparst.protokolle.filter((p) => !bestehendeProtokollIds.has(p.id));
	speichereProtokolle([...bestehendeProtokolle, ...hinzugefuegteProtokolle]);

	return { erfolg: true, neueObjekte: hinzugefuegteObjekte.length, neueProtokolle: hinzugefuegteProtokolle.length };
}
